import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {
  FingerModel,
  FingerScannerModel,
  InitiateEnrollmentRequestModel,
  RegistrationStateModel
} from 'src/app/shared/models/enrollment.model';
import {EnrollmentService} from 'src/app/shared/services/enrollment.service';
import {AlertService} from 'src/app/shared/services/alert.service';
import {ProgressbarConfig} from 'ngx-bootstrap/progressbar';
import {UtilsService} from 'src/app/shared/services/utils.service';
import {StaticResourcesService} from 'src/app/shared/services/static-resources.service';
import {TranslateService} from '@ngx-translate/core';
import {UuidService} from 'src/app/shared/services/uuid.service';
import {asyncScheduler, from, interval, merge, Observable, Subscription, timer} from 'rxjs';
import {map, switchMap, throttleTime} from 'rxjs/operators';

import {initVFace, VFaceWeb} from 'src/assets/vface/vface-web';
import {IdpUrlModel} from 'src/app/shared/models/idp-url.model';
import {NavbarService} from '../../shared/services/navbar.service';
import {FidoEnrollmentForm, FingerprintEnrollmentForm} from "../../shared/models/typed-forms";

declare function registerFidoFromBrowser(startRegistrationResponse);

export function getProgressbarConfig(): ProgressbarConfig {
  return Object.assign(new ProgressbarConfig(), {animate: true, striped: true});
}

export enum Step {
  NONE,
  Main,
  ChooseAuthMethod,
  Advance,
  EnrollAuthMethod,
  EnrollFido,
  EnrollFidoIdp,
  EnrollVface,
  EnrollDesktopTouchId,
  Finished,
  Timeout,
  Failed
}

function isStepInitial(step: Step | undefined) {
  return step == undefined || step == Step.NONE || step == Step.Main;
}

function isStepFinal(step: Step | undefined) {
  return step == Step.Finished || step == Step.Timeout || step == Step.Failed
}


enum Purpose {
  ENROLL = "ENROLL",
  RE_ENROLL_AUTHENTICATOR = "RE_ENROLL_AUTHENTICATOR"
}

const ERROR_KEY_MAP = {
  'The user attempted to register an authenticator that contains one of the credentials already registered with the relying party.': 'ERROR.FIDO_DEVICE_ALREADY_ENROLLED',
  'The operation either timed out or was not allowed. See: https://www.w3.org/TR/webauthn-2/#sctn-privacy-considerations-client.': 'ENROLLMENT.FIDO_ENROLL_CANCELED',
  'Fido authenticator service is not reachable.': 'ERROR.FIDO_SERVICE_AVAILABILITY'
};

@Component({
  selector: 'app-enrollment',
  templateUrl: './enrollment.component.html',
  styleUrls: ['./enrollment.component.scss'],
  providers: [{provide: ProgressbarConfig, useFactory: getProgressbarConfig}]
})
export class EnrollmentComponent implements OnInit, OnDestroy {
  fidoRPs: IdpUrlModel[];

  isLoading: boolean = false;
  initiated: boolean = false;
  purpose: string = Purpose.ENROLL;
  restartBtnHiddenByPreviousStep: boolean = false;
  restartBtnAvailable: boolean = false;
  restartBtnDisabled: boolean = false;
  lastEnrollmentResponse: any;

  username: string;
  encodedLoginData: string;
  redirectUrl: string;
  authMethod: string;
  pendingEnrollmentMethods: Array<string> = [];
  identityId: string;
  enrollmentTrackerId: string;
  nxtBtnTxt: string = "Save";
  restartBtnTxt: string = "Retry";

  authenticationMethods: any;
  registrationQR: any;
  swOtpQr: any;
  swOtpSecretKey: string;

  fingerScanners: FingerScannerModel[];
  fingers: FingerModel[];
  fingerprintData: string;
  fingerprintImage: string;
  fingerScannerMessage: string;
  selectedScanner: FingerScannerModel;
  selectedFinger: number = 2;

  fidoEnrollmentForm: FormGroup<FidoEnrollmentForm>;
  fingerprintEnrollmentForm: FormGroup<FingerprintEnrollmentForm>;
  fidoIdpForm: FormGroup;
  dynamicForm: FormGroup;

  step: Step = Step.NONE;
  Step = Step;

  configs: any;
  authMethodGuiConfig: any;
  biometricTemplate: string;

  retryStepSub: Subscription;
  schema: RegistrationStateModel;

  fidoKeys: Array<string>;
  fidoEnrollmentExternalScriptDone: boolean = false;

  vface: any;

  maxResendCode: number = 3;
  secondsBetweenResends: number = 5;
  secondsBetweenResendsLeft = this.secondsBetweenResends;
  resendsLeft: number = this.maxResendCode;

  enrollmentTimeoutSec: number = 300;
  enrollmentTimeoutSubscription: Subscription;

  selfInitEnrollment = true;

  inputType = [];

  vfaceNonce: string;
  selectedDeviceNm: string;


  constructor(
    private readonly enrollmentService: EnrollmentService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly alertService: AlertService,
    private readonly utilsService: UtilsService,
    public readonly translate: TranslateService,
    private readonly uuidService: UuidService,
    private readonly staticResourcesService: StaticResourcesService,
    private readonly navbarService: NavbarService,
    private readonly fb: FormBuilder
  ) {
  }

  ngOnDestroy() {
    if (this.retryStepSub) {
      this.retryStepSub.unsubscribe();
    }
    this.stopTimeEnrollment();
  }

  ngOnInit(): void {
    this.staticResourcesService.fetchConfigs().subscribe(
      configs => {
        this.configs = configs;
        this.fidoRPs = this.configs.fidoRpList;
        this.maxResendCode = configs?.maxResendCode;
        this.resendsLeft = this.maxResendCode;
        this.enrollmentTimeoutSec = configs?.enrollmentTimeout;
      },
      error => {
        console.log(error);
      }
    );


    // subscribe for url changes: path or query. handle only trailing event (if many) since we reinitialize all.
    merge(this.route.params, this.route.queryParams)
      .pipe(throttleTime(100, asyncScheduler, {leading: false, trailing: true}))
      .subscribe(() => {
        this.onReset()
      })

  }

  //this is called after the username is filled and an enrollment session is started. if time is up, redirects the user to the first step
  timeEnrollment() {
    this.stopTimeEnrollment()

    this.enrollmentTimeoutSubscription = timer(
      this.enrollmentTimeoutSec * 1000
    ).subscribe(() => {
      this.onTimeout()
    });
  }

  stopTimeEnrollment() {
    if (this.enrollmentTimeoutSubscription) {
      this.enrollmentTimeoutSubscription.unsubscribe();
      this.enrollmentTimeoutSubscription = null;
    }
  }

  getCurrentAppLanguage() {
    if (this.navbarService.language) {
      return this.navbarService.language;
    } else if (this.translate.getBrowserLang()) {
      return this.translate.getBrowserLang();
    } else {
      return 'en';
    }
  }

  initVFace(newImplementation: boolean) {
    const currentAppLanguage = this.getCurrentAppLanguage();
    const vFaceConfig = this.configs.vFaceConfig;

    let vFaceLanguageObject = vFaceConfig?.translations[currentAppLanguage] || null;
    if (!vFaceLanguageObject) {
      console.warn(`No vFace translation provided for ${currentAppLanguage}`);
    }
    if (!vFaceConfig?.license) {
      console.warn(`vFace initialization failed.`);
    }

    const vFaceTranslations = this.getVFaceTranslations(vFaceLanguageObject);
    newImplementation ? this.initNewVFaceLibrary(vFaceTranslations, vFaceConfig) : this.initOldVFaceLibrary(vFaceTranslations, vFaceConfig);
    console.info('vFace Web Version ' + this.vface.version());
  }

  getVFaceTranslations(vFaceLanguageObject) {
    if (vFaceLanguageObject) {
      return Object.keys(vFaceLanguageObject).filter(key =>
        !key.startsWith('ERROR_')).reduce((obj, key) => {
          obj[key] = vFaceLanguageObject[key];
          return obj;
        }, {}
      );
    } else {
      return null;
    }
  }

  initNewVFaceLibrary(vFaceTranslations, vFaceConfig) {
    console.info('Using the new implementation of vFace')
    if (vFaceTranslations) {
      this.vface = initVFace({
        licence: vFaceConfig?.license,
        localisation: vFaceTranslations
      });
    } else {
      this.vface = initVFace({
        licence: vFaceConfig?.license
      });
    }
  }

  initOldVFaceLibrary(vFaceTranslations, vFaceConfig) {
    console.info('Using the old implementation of vFace');
    if (vFaceTranslations) {
      this.vface = new VFaceWeb({
        licence: vFaceConfig?.license,
        localisation: vFaceTranslations
      });
    } else {
      this.vface = new VFaceWeb({
        licence: vFaceConfig?.license
      });
    }
  }

  init() {
    this.initEnroll(Purpose.ENROLL);
  }

  initResetPin() {
    this.authMethod = "PIN";
    this.initEnroll(Purpose.RE_ENROLL_AUTHENTICATOR);
  }

  initResetLdapPassword(reEnroll: boolean) {
    this.authMethod = "LDAP_PASSWORD";
    this.initEnroll(reEnroll ? Purpose.RE_ENROLL_AUTHENTICATOR : Purpose.ENROLL);
  }

  initEnrollTotpDesktop() {
    this.initEnroll(Purpose.ENROLL);
    this.selectAuthMethod('TOTP_DESKTOP');
  }

  initEnrollDesktopBiometrics() {
    this.initEnroll(Purpose.ENROLL);
    this.selectAuthMethod('WBF');
  }

  initDelegatedFidoEnrollment(enrollmentTrackerId: string, identityId: string) {
    this.authMethod = "FIDO";
    this.enrollmentTrackerId = enrollmentTrackerId;
    this.identityId = identityId;

    this.initiated = true;
    this.initFidoEnrollmentForm();
    this.step = Step.EnrollFido;

    this.loadExistingFIDOKeys();
    this.startFIDOEnrollment();
  }

  initEnroll(purpose) {
    this.purpose = purpose;
    let initiateEnrollmentReq: InitiateEnrollmentRequestModel = {
      purpose: purpose
    }

    this.isLoading = true;
    this.enrollmentService.initiateEnrollment(initiateEnrollmentReq)
      .subscribe((resp) => {
        if (resp?.error?.errorCode !== 0) {
          let errorMessage: string = resp?.error?.errorDescription || "Failed to initiate enrollment";
          this.alertService.error(errorMessage);
          this.isLoading = false;
        } else {
          this.schema = resp.registrationState;
          this.step = this.loopEnrollment(resp)
          this.isLoading = false;

          if (this.step == Step.Main && this.encodedLoginData) {
            this.initiated = false;
            const invitationData = JSON.parse(atob(this.encodedLoginData));
            this.dynamicForm.patchValue(invitationData);
            this.onSubmitDynamicForm();
          } else {
            this.initiated = true;
          }
        }
      }, error => {
        this.isLoading = false;
      });
  }

  initDynamicForm() {
    const group: any = {};

    let formDefaultValues = this.buildFormDefaultValues(this.schema, group);

    this.dynamicForm = new FormGroup(group);

    //if there are default values, set them on inputs
    this.dynamicForm.patchValue(formDefaultValues);

    if (this.utilsService.isNullOrEmpty(this.schema.enrollmentStepActionName)) {
      this.nxtBtnTxt = "ENROLLMENT.SAVE";
    } else {
      this.nxtBtnTxt = this.schema.enrollmentStepActionName;
    }

    if (
      this.utilsService.isNullOrEmpty(
        this.schema.restartEnrollmentStepActionName
      )
    ) {
      this.restartBtnTxt = "ENROLLMENT.RETRY";
      this.restartBtnAvailable = false;
    } else {
      this.restartBtnTxt = this.schema.restartEnrollmentStepActionName;
      this.restartBtnAvailable = true;
    }
  }

  toggleResendPinForPersonalAndManager(formValue) {
    //check if the current step should hide the resend code button on the next screen
    if (formValue?.receiver && (formValue?.receiver == "LINE_MANAGER" || formValue?.receiver == "PERSONAL")) {
      this.restartBtnHiddenByPreviousStep = false;
    } else {
      this.restartBtnHiddenByPreviousStep = true;
    }
  }

  buildFormDefaultValues(schema: any, group: any) {
    let formDefaultValues: any = {};
    schema.parameters.forEach(field => {

      const validators = [];

      if (field.required) {
        validators.push(Validators.required);
      }

      const pattern = field['pattern'];
      if (pattern) {
        validators.push(Validators.pattern(pattern));
      }

      group[field.name] = new FormControl<string>('', validators);

      if (field.value != null) {
        formDefaultValues[field.name] = field.value;
      }

      if (field.name == 'username') {
        formDefaultValues['username'] = this.username;
      }

      //if select input, choose first option as default
      if (field.controlType == 'enum') {
        formDefaultValues[field.name] = field.value[0];
      }
    });

    return formDefaultValues;
  }

  schemaParameterNameEquals(value) {
    return this.schema.parameters[0].name && this.schema.parameters[0].name === value;
  }

  schemaHasParameters(schema) {
    return schema?.parameters && schema?.parameters?.length === 1;
  }

  onSubmitDynamicForm() {
    if (this.step == Step.Main) {
      this.username = this.dynamicForm.value?.username;
    }

    if (this.step == Step.Main || this.step == Step.ChooseAuthMethod || this.step == Step.Advance) {
      this.continueEnrollment();
    }

    if (this.step == Step.EnrollAuthMethod) {
      this.continueSetAuthMethod(this.dynamicForm.value);
    }
  }

  private continueEnrollment() {
    this.isLoading = true;

    const formValue = this.dynamicForm ? this.dynamicForm.value : null;

    this.enrollmentService.continueEnrollment(this.authMethod, this.identityId, formValue, this.enrollmentTrackerId, this.purpose)
      .subscribe(
        resp => this.handleEnrollmentResponse(resp, formValue),
        error => this.logEnrollmentError(error)
      );
  }

  private handleEnrollmentResponse(resp: any, formValue: any) {
    this.isLoading = false;
    this.initiated = true;

    if (resp.error.errorCode !== 0) {
      this.processEnrollmentError(resp.error, formValue);
    } else {
      this.processSuccessfulEnrollment(resp, formValue);
    }
  }

  private processEnrollmentError(error: any, formValue: any) {
    let errorMessage = 'An error occurred while enrolling method.';
    this.translate.get('ERROR.ENROLL_METHOD', {value: errorMessage})
      .subscribe((res: string) => errorMessage = res);

    this.alertService.error(error.errorDescription, errorMessage);

    switch (error.errorCode) {
      case 172:
        // Illegal enrollment state, reset wizard
        this.onFail();
        break;
      case 171:
        // Enrollment expired
        this.onTimeout();
        break;
    }
  }

  private processSuccessfulEnrollment(resp: any, formValue: any) {
    if (this.step === Step.Main) {
      // Saved response to use after setAuthMethod
      this.step = this.loopEnrollment(resp);
    } else {
      this.toggleResendPinForPersonalAndManager(formValue);
      this.step = this.loopEnrollment(resp);
    }
  }

  private logEnrollmentError(error: any) {
    this.isLoading = false;
    console.log("Error in step: " + this.step + " " + error);
  }

  private continueSetAuthMethod(data: any) {
    this.isLoading = true;
    this.enrollmentService.continueSetAuthMethod(this.authMethod, this.identityId, data, this.enrollmentTrackerId)
      .subscribe(resp => {
        this.isLoading = false;
        if (resp.error.errorCode !== 0) {
          let errorMessage = 'An error occurred while setting authentication method.';
          this.translate
            .get('ERROR.AUTH_METHODS', {value: errorMessage})
            .subscribe((res: string) => {
              errorMessage = res;
            });
          this.alertService.error(resp.error.errorDescription,);
          if (resp.error.errorCode === 172) {
            this.router.navigate(['authenticators']);
          }
        } else if (this.purpose == Purpose.RE_ENROLL_AUTHENTICATOR) {
          this.authenticationMethods = null;
          this.step = Step.Finished;
        } else {
          this.loopSetAuthMethod(resp);
        }
      }, error => {
        this.isLoading = false;
        console.log("ERROR: " + error.errorDescription);
      });
  }

  selectAuthMethod(authMethod: string) {
    if (authMethod == 'FIDO' && this.authenticationMethods?.find(method => method.name == 'PIN')?.required) {
      this.pendingEnrollmentMethods.push(authMethod);
      this.authMethod = 'PIN';
    } else {
      this.authMethod = authMethod;
    }
    this.toggleResendPinForPersonalAndManager(this.dynamicForm?.value);
    this.step = this.loopEnrollment(this.lastEnrollmentResponse);
  }

  private loopEnrollment(resp: any): Step {
    const prevStep = this.step;
    this.lastEnrollmentResponse = resp;

    // Extracting repeated response assignments to separate functions
    this.updateProfileAndAuthDetails(resp);

    if (!resp.currentStatus) {
      this.startEnrollment(resp);
    } else if (!this.authMethod) {
      this.chooseAuthMethod(resp);
    } else if (resp.currentStatus !== 'ACTIVE') {
      this.advanceEnrollment(resp);
    } else {
      this.completeEnrollment(resp);
    }

    if (this.isInitialButNotFinalStep(prevStep)) {
      this.startEnrollmentTimer();
    }

    return this.step;
  }

  private updateProfileAndAuthDetails(resp: any): void {
    if (resp.profileId) {
      this.identityId = resp.profileId;
    }
    if (resp.enrollmentTrackerId) {
      this.enrollmentTrackerId = resp.enrollmentTrackerId;
    }
    if (resp.authMethodConfiguration) {
      this.authMethodGuiConfig = JSON.parse(resp.authMethodConfiguration);
    }
    if (resp.authenticationMethods) {
      this.authenticationMethods = resp.authenticationMethods;
    }
    if (resp.registrationQR) {
      this.registrationQR = resp.registrationQR;
    }
  }

  private startEnrollment(resp: any): void {
    this.schema = resp.registrationState;
    this.initDynamicForm();
    this.step = Step.Main;
  }

  private chooseAuthMethod(resp: any): void {
    this.lastEnrollmentResponse = resp;
    this.step = Step.ChooseAuthMethod;
  }

  private advanceEnrollment(resp: any): void {
    this.schema = resp.registrationState;
    this.initDynamicForm();
    this.step = Step.Advance;
  }

  private completeEnrollment(resp: any): void {
    switch (this.authMethod) {
      case "FIDO":
        this.enrollFido();
        break;
      case "SMS":
      case "PIN":
      case "TOTP_DESKTOP":
      case "YUBICO_OTP":
      case "SW_OTP":
      case "HW_OTP":
      case "LDAP_PASSWORD":
      case "WBF":
        this.enrollAuthMethod();
        break;
      case 'DESKTOP_FACE':
        this.enrollVFace();
        break;
      case 'DESKTOP_FINGERPRINT':
        this.enrollDesktopFingerprint();
        break;
      default:
        break;
    }
  }

  private enrollFido(): void {
    this.initFidoEnrollmentForm();
    if (!this.configs.delegateFidoEnrollment) {
      this.step = Step.EnrollFido;
      this.loadExistingFIDOKeys();
      this.startFIDOEnrollment();
    } else if (this.fidoRPs?.length === 1) {
      this.handleFidoRegistrationRedirect(this.fidoRPs[0]);
    } else {
      this.initFidoIdpForm();
    }
  }

  private enrollAuthMethod(): void {
    if (this.authMethod === "WBF") {
      this.step = Step.EnrollAuthMethod
      return
    }
    this.schema = this.authMethodGuiConfig[this.authMethod].guiConfiguration;
    this.initDynamicForm();
    if (this.authMethod === "SW_OTP" && this.dynamicForm.controls['otpType']) {
      const options = this.authMethodGuiConfig['SW_OTP'].options;
      this.updateQrCodeForSwOtpEnrolment(this.dynamicForm.controls['otpType'].value, options);
      this.dynamicForm.controls['otpType'].valueChanges.subscribe(value => {
        this.updateQrCodeForSwOtpEnrolment(value, options);
      });
    }
    this.step = Step.EnrollAuthMethod;
  }

  private enrollVFace(): void {
    this.step = Step.EnrollVface;
    this.startVFaceEnrolment();
  }

  private enrollDesktopFingerprint(): void {
    this.biometricTemplate = this.authMethodGuiConfig['DESKTOP_FINGERPRINT'].options['biometricTemplate'];
    this.step = Step.EnrollDesktopTouchId;
    this.manageDesktopTouchIdEnrollment();
  }

  private isInitialButNotFinalStep(prevStep: Step): boolean {
    return isStepInitial(prevStep) &&
      !isStepInitial(this.step) && !isStepFinal(this.step);
  }

  private startEnrollmentTimer(): void {
    this.timeEnrollment();
  }

  loadExistingFIDOKeys() {
    this.enrollmentService.getFidoKeyNames(this.identityId).subscribe(
      response => {
        this.fidoKeys = response.fidoKeys;

        //patch existing fido enrollment with first option
        if (this.fidoKeys && this.fidoKeys.length > 0) {
          this.fidoEnrollmentForm.patchValue({
            selectedExistingKey: this.fidoKeys[0]
          });
          this.selectedDeviceNm = this.fidoKeys[0];
          this.fidoEnrollmentForm.get('selectedName').setValue(this.fidoKeys[0]);
        }
      },
      error => {
        console.log(error);
      }
    );
  }

  getErrorKey(errorMessage) {
    return ERROR_KEY_MAP[errorMessage] || errorMessage;
  }

  startFIDOEnrollment() {
    this.enrollmentService
      .startFidoRegistration(
        this.enrollmentTrackerId
      )
      .pipe(switchMap(response => {
        if (response.error) {
          console.log(response.error);
          throw new Error(response.error.errorDescription);
        } else {
          return from(registerFidoFromBrowser(response));
        }
      }))
      .subscribe(
        response => {
          this.onUpdateFIDODetails();
        },
        error => {
          console.log(error);
          let errorMessage = error.message;
          const key = this.getErrorKey(errorMessage);

          this.translate
            .get(key, { value: errorMessage })
            .subscribe((res: string) => {
              errorMessage = res;
              this.alertService.error(errorMessage);
            });
          this.onFail();
        }
      );
  }

  initFidoEnrollmentForm() {
    this.fidoEnrollmentExternalScriptDone = false;
    this.fidoEnrollmentForm = this.fb.group<FidoEnrollmentForm>({
      registrationOption: new FormControl<string>('new', [Validators.required]),
      selectedExistingKey: new FormControl<string>(''),
      selectedName: new FormControl<string>(''),
      customName: new FormControl<string>(''),
    })
  }

  //triggered from FIDO external js script
  onUpdateFIDODetails() {
    this.fidoEnrollmentExternalScriptDone = true;
    this.fidoEnrollmentForm.patchValue({
      customName: document.getElementById("newDeviceName")["value"]
    });
  }

  onDeviceNameSelection($event): void {
    console.log($event.target.value);
    this.fidoEnrollmentForm.get('selectedName').setValue($event.target.value);
  }

  onSubmitFidoEnrollmentForm() {
    let keyData = document.getElementById("registerSessionFinish")["value"];

    //get device name based on user option
    let deviceName: string = "";
    let newDeviceName: string = "";
    const registrationOption = this.fidoEnrollmentForm.value.registrationOption;
    const useExistingDevice = (registrationOption === 'existing');
    if (useExistingDevice) {
      deviceName = this.fidoEnrollmentForm.value.selectedExistingKey;
      newDeviceName = this.fidoEnrollmentForm.value.selectedName;
    } else {
      deviceName = this.fidoEnrollmentForm.value.customName;
    }

    this.enrollmentService
      .finishFidoRegistration(deviceName, newDeviceName, useExistingDevice, this.enrollmentTrackerId, keyData, this.identityId)
      .subscribe(
        response => {
          if (response.error.errorCode === 0) {
            this.step = Step.Finished;
          } else {
            let errorMessage: string = (response.error.errorDescription === null) ?
              'An error occurred while setting authentication method.' : response.error.errorDescription;
            const key = 'ERROR.FIDO_GENERIC_ENROLLMENT_ERROR';
            this.translate
              .get(key, { value: errorMessage })
              .subscribe((res: string) => {
                if (res !== key) {
                  errorMessage = res;
                }
                this.alertService.error(errorMessage);
              });
            console.error("Failed to finish registration for a FIDO key", response.error);
            this.onFail();
          }
        },
        error => {
          console.log(error);
        }
      );
  }

  createBioMatchSessionSignedConfig(): Observable<any> {
    const config = {
      debug: this.configs?.vFaceConfig.enableDebug,
      license: this.configs?.vFaceConfig.license,
      liveness: this.configs?.vFaceConfig.enableLiveness,
      liveness_factor: this.configs?.vFaceConfig.livenessFactor
    }

    return this.enrollmentService.createBiometricSession({
      config: JSON.stringify(config),
      nonce: this.vfaceNonce,
      type: 'VFACE'
    })
      .pipe(map(value => value));
  }

  startVFaceEnrolment() {
    this.vfaceNonce = this.uuidService.generateUUID(); // session id
    const requiredBiometricSession = this.configs?.vFaceConfig?.requiredBiometricSession;
    if (requiredBiometricSession === true) {
      this.initVFace(true);
      this.enrolVFaceNew();
    } else {
      this.initVFace(false);
      this.enrolVFaceOld();
    }
  }

  enrolVFaceNew() {
    this.createBioMatchSessionSignedConfig()
      .subscribe(
        (res) => this.handleSignedConfigResponse(res),
        (error) => this.handleEnrollmentError(error)
      );
  }

  handleSignedConfigResponse(response) {
    if (response?.error?.errorCode !== 0) {
      let errorMessage =
        'An error occurred while creating biometric session signed config.';
      this.translate
        .get('ERROR.SIGNED_CONFIG', {value: errorMessage})
        .subscribe((res: string) => {
          errorMessage = res;
          this.alertService.error(errorMessage);
        });
      console.error("Failed to create biometric session signed config", response.error);
      this.onFail();
    }
    const base64SignedConfig = response?.signedConfig;
    const signedConfigBuffer = this.stringToArrayBufferPromise(base64SignedConfig);

    this.vface.enroll(signedConfigBuffer)
      .then((encryptedTemplateBuffer) => this.handleEncryptedTemplate(encryptedTemplateBuffer))
      .catch((error) => this.handleEnrollmentError(error));
  }

  handleEncryptedTemplate(encryptedTemplateBuffer) {
    const base64EncryptedTemplate = this.arrayBufferToBase64(encryptedTemplateBuffer);
    if(base64EncryptedTemplate === "" || base64EncryptedTemplate ===  undefined) {
      this.alertService.error("VFace biometric vector is empty. Please enroll again", "Empty VFace biometric vector")
      return;
    }
    const enrollmentData = {
      isTemplateEncrypted: true,
      biometrics: base64EncryptedTemplate,
      nonce: this.vfaceNonce
    };

    this.continueSetAuthMethod(enrollmentData);
  }

  handleEnrollmentError(errorObj) {
    if (errorObj?.error === 4) {
      // User canceled, do nothing
      return;
    }

    const vfaceErrorCode = errorObj?.error;
    let errorMessage = 'Default error message';

    if (this.configs?.vFaceConfig.translationKeysByErrorCode[vfaceErrorCode] !== undefined) {
      const translationKey = this.configs?.vFaceConfig.translationKeysByErrorCode[vfaceErrorCode];
      const translations = this.configs?.vFaceConfig.translations[this.getCurrentAppLanguage()];

      if (translations !== undefined) {
        errorMessage = translations[translationKey];
      }
    }

    this.alertService.error(errorMessage);
    this.initVFace(true);
    this.onFail();
  }

  enrolVFaceOld() {
    let json_config_string = JSON.stringify({
        debug: this.configs?.vFaceConfig.enableDebug,
        liveness: this.configs?.vFaceConfig.enableLiveness,
        liveness_factor: this.configs?.vFaceConfig.livenessFactor
      }
    )
    this.vface.enroll({json_config: json_config_string}).then(
      (vector: string) => {
        let rawBiometricVector: string = vector.replace("data:application/octet-stream;base64,", "");
        if(rawBiometricVector === "" || rawBiometricVector === undefined) {
          this.alertService.error("VFace biometric vector is empty. Please enroll again", "Empty VFace biometric vector")
          return;
        }
        let enrollmentData: any = {
          isTemplateEncrypted: false,
          biometrics: rawBiometricVector
        }
        this.continueSetAuthMethod(enrollmentData);
      },
      async (errorObj: any) => {
        console.log(errorObj);

        if (errorObj?.error !== 4) {
          // user didn't cancel, show error

          const vfaceErrorCode = errorObj?.error;
          let errorMessage = await this.translate.get('ERROR.AUTH_METHODS', {value: 'There was an error capturing the face biometry.'}).toPromise();

          if (this.configs?.vFaceConfig.translationKeysByErrorCode[vfaceErrorCode] !== undefined) {
            const translationKey = this.configs?.vFaceConfig.translationKeysByErrorCode[vfaceErrorCode];
            if (this.configs?.vFaceConfig.translations[this.getCurrentAppLanguage()] !== undefined) {
              errorMessage = this.configs?.vFaceConfig.translations[this.getCurrentAppLanguage()][translationKey];
            }
          }

          this.alertService.error(errorMessage);
        }
        //reinit vface library due to vface embedded-CSS related issues
        this.initVFace(false);
        this.onFail();
      }
    );
  }

  initFidoIdpForm() {
    const group: any = {};
    group['idp'] = new FormControl('', Validators.required);
    this.fidoIdpForm = new FormGroup(group);
    this.step = Step.EnrollFidoIdp;

    //select first option
    this.fidoIdpForm.patchValue({
      idp: this.fidoRPs[0]
    });
  }

  onSubmitFidoIdpForm() {
    let idp = this.fidoIdpForm.value.idp;
    this.handleFidoRegistrationRedirect(idp);
  }

  handleFidoRegistrationRedirect(idp: any) {
    let redirectTo: string = "";
    redirectTo = idp.url +
      "?enrollmentTrackerId=" + this.enrollmentTrackerId +
      "&authenticator=FIDO" +
      "&identityId=" + this.identityId +
      "&username=" + this.username +
      "&" + idp.idQueryParam + "=" + encodeURIComponent(this.configs.providerId);
    window.location.href = redirectTo;
  }

  loopSetAuthMethod(resp: any) {
    this.authenticationMethods = resp.authenticationMethods;
    if (this.pendingEnrollmentMethods.length > 0) {
      this.selectAuthMethod(this.pendingEnrollmentMethods.pop())
    } else if (resp.deviceStatus === 'ACTIVATED') {
      this.step = Step.Finished;
    }
  }

  onRestartStep() {
    if (this.retryStepSub) {
      this.retryStepSub.unsubscribe();
    }

    this.resendsLeft--;
    this.restartBtnDisabled = true;

    //timer to prevent multi send between sends
    this.retryStepSub = interval(1000).subscribe(count => {
      if (this.secondsBetweenResendsLeft > 0) {
        this.secondsBetweenResendsLeft--;
      } else {
        if (this.resendsLeft > 0) {
          this.restartBtnDisabled = false;
        }
        this.secondsBetweenResendsLeft = this.secondsBetweenResends;
      }
    });

    // usually, repeat the current step
    this.isLoading = true;
    this.enrollmentService
      .restartStep(this.identityId, this.enrollmentTrackerId)
      .subscribe(
        resp => {
          if (resp.error.errorCode == 2004) {
            //retries reached, reset wizard
            let errorMessage = 'An error occurred while enrolling method.';
            this.translate
              .get('ERROR.ENROLL_METHOD', {value: errorMessage})
              .subscribe((res: string) => {
                errorMessage = res;
              });
            this.alertService.error(resp.error.errorDescription, errorMessage);
            this.onFail();
          }
          this.isLoading = false;
        },
        error => {
          this.isLoading = false;
          console.log(this.step)
        }
      );
  }

  onTimeout() {
    if (!isStepFinal(this.step)) {
      this.step = Step.Timeout;
    }
  }

  onFail() {
    this.step = Step.Failed;
  }

  onReset() {
    this.schema = null;
    this.authMethod = null;
    this.identityId = null;
    this.enrollmentTrackerId = null;
    this.initiated = false;
    this.resendsLeft = this.maxResendCode;

    this.step = Step.NONE;
    this.purpose = Purpose.ENROLL;
    this.restartBtnAvailable = false;
    this.restartBtnDisabled = false;
    this.lastEnrollmentResponse = null;
    this.registrationQR = null;

    this.stopTimeEnrollment();

    this.inputType = [];

    this.username = this.route.snapshot.paramMap.get('username');

    const queryParams = this.route.snapshot.queryParams;
    this.encodedLoginData = queryParams['invitationData'];

    let redirectUrlRaw = queryParams['redirectUrl'];
    if (redirectUrlRaw != null) {
      let redirectUrlBase64: string = decodeURI(queryParams['redirectUrl']);
      this.redirectUrl = atob(redirectUrlBase64);
    }

    //in case of reset pin action
    const action = queryParams['action'];
    if (action) {
      if (action === 'RESET_PIN') {
        this.initResetPin();
      } else if (action === 'TOTP_DESKTOP') {
        this.initEnrollTotpDesktop();
      } else if (action === 'LDAP_PASSWORD_RESET') {
        this.initResetLdapPassword(false);
      } else if (action === 'LDAP_PASSWORD_RENEW') {
        this.initResetLdapPassword(true);
      } else if (action === 'WBF') {
        this.initEnrollDesktopBiometrics();
      }
    } else if (queryParams['authenticator'] == 'FIDO' && queryParams['enrollmentTrackerId']) {
      this.initDelegatedFidoEnrollment(queryParams['enrollmentTrackerId'], queryParams['identityId']);
    } else if (this.username) {
      this.selfInitEnrollment = false;
    } else {
      this.init();
    }
  }

  finish(isComplete: boolean) {
    if (isComplete) {
      this.router.navigate(['authenticators']);
    } else {
      this.step = Step.ChooseAuthMethod;
    }
  }

  getInputType(fieldName) {
    if (!this.inputType[fieldName]) {
      this.inputType[fieldName] = 'password';
      return 'password';
    }
    return this.inputType[fieldName];
  }

  changeInputType(fieldName) {
    if (this.inputType[fieldName] === 'password') {
      this.inputType[fieldName] = 'text';
    } else if (this.inputType[fieldName] === 'text') {
      this.inputType[fieldName] = 'password';
    }
  }

  updateQrCodeForSwOtpEnrolment(type: string, options: any) {
    this.swOtpSecretKey = this.genBase32Rand(32, ['A', 'Z'], ['2', '7']);
    let url = new URL(`otpauth://${type.toLowerCase()}/${this.configs?.serverName}:${this.username}`);
    url.searchParams.append('secret', this.swOtpSecretKey);
    url.searchParams.append('issuer', this.configs?.serverName);
    if (type === 'TOTP') {
      let algorithm = options?.totpAlgorithm?.replace(/[^a-zA-Z\d]/g, '');
      url.searchParams.append('algorithm', algorithm);
      url.searchParams.append('period', options?.totpValidity);
      url.searchParams.append('digits', options?.totpLength);
    } else if (type === 'HOTP') {
      url.searchParams.append('digits', options?.totpLength);
      url.searchParams.append('counter', '0');
    }

    if (this.dynamicForm.controls['secretKey']) {
      this.dynamicForm.controls['secretKey'].setValue(this.swOtpSecretKey);
    }
    this.swOtpQr = encodeURI(url.toString());
  }

  genBase32Rand(length, ...ranges) {
    let str = "";
    while (length--) {
      const ind = Math.floor(Math.random() * ranges.length);
      const min = ranges[ind][0].charCodeAt(0),
        max = ranges[ind][1].charCodeAt(0);
      const c = Math.floor(Math.random() * (max - min + 1)) + min;
      str += String.fromCharCode(c);
    }
    return str;
  }

  stringToArrayBufferPromise(inputString: string) {
    return new Promise((resolve, reject) => {
      const arrayBuffer = this.base64ToArrayBuffer(inputString);
      resolve(arrayBuffer);
    });
  }

  base64ToArrayBuffer(base64: string) {
    let fromUrl = base64.replace(/-/g, '+').replace(/_/g, '/');
    let binary_string = window.atob(fromUrl);
    let len = binary_string.length;
    let bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  }

  arrayBufferToBase64(arrayBuffer) {
    const uint8Array = new Uint8Array(arrayBuffer);
    let binaryString = '';
    uint8Array.forEach(byte => {
      binaryString += String.fromCharCode(byte);
    });
    return btoa(binaryString);
  }

  manageDesktopTouchIdEnrollment() {
    this.translate
      .get('ENROLLMENT.LOOKING_FOR_FSCANNER', {})
      .subscribe((res: string) => {
        this.fingerScannerMessage = res;
      });

    this.fingerprintEnrollmentForm = this.fb.group<FingerprintEnrollmentForm>({
      fingerScanners: new FormControl<string>('', Validators.required),
      fingerSelect: new FormControl<string>('', Validators.required),
    })

    this.enrollmentService.getFingerScanners()
      .subscribe(resp => {
          this.fingerScanners = resp;
          if (resp.length > 0) {
            this.selectedScanner = resp[0];
            this.selectedFinger = 2;
            this.translate
              .get('ENROLLMENT.FOUND_FSCANNER', {})
              .subscribe((res: string) => {
                this.fingerScannerMessage = `${res} (${resp[0].deviceName}).`;
              });
          } else {
            this.translate
              .get('ENROLLMENT.NOT_FOUND_FSCANNER', {})
              .subscribe((res: string) => {
                this.fingerScannerMessage = res;
              });
          }
        },
        (error) => {
          this.translate
            .get('ENROLLMENT.NOT_FOUND_FSCANNER', {})
            .subscribe((res: string) => {
              this.fingerScannerMessage = res;
            });
          this.translate
            .get('ENROLLMENT.NOT_FOUND_FSCANNER_SERVICE', {})
            .subscribe((res: string) => {
              this.alertService.error(res);
            });
        })
    this.enrollmentService.getFingerPrints()
      .subscribe(resp => {
        this.fingers = resp;
      })
  }

  scanFingerprint() {
    this.enrollmentService.scanFingerprint(this.selectedFinger, this.biometricTemplate).subscribe(
      resp => {
        if (resp.length != 0) {
          this.fingerprintData = resp[0].Outputs[0].Base64Data;
          this.fingerprintImage = `data:image/bmp;base64,${this.fingerprintData}`;
        }
      }
    );
  }

  registerFingerprint() {
    if(this.fingerprintData === "" || this.fingerprintData === undefined) {
      this.alertService.error("Fingerprint biometric vector is empty. Please enroll again", "Empty fingerprint biometric vector")
      return;
    }
    let enrollmentData: any = {biometrics: this.fingerprintData, template: this.biometricTemplate};
    this.continueSetAuthMethod(enrollmentData);
  }

}
